import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import { BOAR_HIP_Y, BOAR_LEGS, boarBody, boarLeg } from '../models/boar.ts';
import { facingToRotY, SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import type { OmenCtx } from '../omens/common.ts';

/**
 * The drive (El Cuerno 1, docs/SAGAS.md): the clan's stone boars trotting across the tail, shying from the heroes and
 * bolting from the nightmares, into the corral (omens/tail.ts builds the corral itself). A boar trots when it moves,
 * gallops head down when it bolts or stampedes (dust kicked up behind it); a small bar over it once it is hurt; a pale
 * pulse round the corral while boars are still out. Penned: a puff of hay and a flare; fallen: it crumbles to rubble.
 *
 * Draw calls: one instanced body (three stone variants share it through instance colours), one instanced leg, the
 * corral's ring and one glow layer. No lights.
 */

const K = WORLD_PER_HEX / 1000;
const MAX = 16;
const BIG = 1.55;
const PEN = new THREE.Color('#e4ecf4');
const DUST = '#8a8478';
/** A slight shift of stone per boar, so the herd isn't twelve of one. */
const TINTS = ['#ffffff', '#e6ebf2', '#f2eee8', '#dfe5e2', '#eef2f6'].map((c) => new THREE.Color(c));

interface Track {
  px: number;
  py: number;
  cx: number;
  cy: number;
  dist: number;
  /** Was it running hard last tick (for the gallop). */
  hard: boolean;
}

export class HerdLayer {
  readonly group = new THREE.Group();
  private bodies: THREE.InstancedMesh;
  private legs: THREE.InstancedMesh;
  private mat: THREE.MeshStandardMaterial;
  private ring: THREE.Mesh;
  private ringMat: THREE.MeshBasicMaterial;
  private glow: GlowLayer;
  private track = new Map<number, Track>();
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private e = new THREE.Euler();
  private now = 0;
  private pen: THREE.Vector3;
  private out = true;

  constructor(
    private ctx: OmenCtx,
    pen: { x: number; y: number },
    penR: number,
  ) {
    this.group.name = 'herd';
    this.mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.88, flatShading: true });
    this.bodies = new THREE.InstancedMesh(boarBody(3), this.mat, MAX);
    this.bodies.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < MAX; i++) this.bodies.setColorAt(i, TINTS[i % TINTS.length] as THREE.Color);
    this.bodies.count = 0;
    this.bodies.frustumCulled = false;
    this.group.add(this.bodies);
    this.legs = new THREE.InstancedMesh(boarLeg(), this.mat, MAX * 4);
    this.legs.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.legs.count = 0;
    this.legs.frustumCulled = false;
    this.group.add(this.legs);
    // The corral's mark on the ground: a pale ring of the radius that counts, breathing while boars are still out.
    this.pen = ctx.at(pen.x, pen.y, 0);
    const r = penR * K;
    this.ringMat = new THREE.MeshBasicMaterial({ color: PEN, transparent: true, opacity: 0.3, depthWrite: false });
    this.ring = new THREE.Mesh(new THREE.RingGeometry(r * 0.88, r, 48).rotateX(-Math.PI / 2), this.ringMat);
    // Over the corral's floor (laid just above the highest turf under it, omens/tail.ts).
    let top = -Infinity;
    for (let i = 0; i <= 24; i++) for (const k of [0, 0.5, 1]) top = Math.max(top, ctx.ground(this.pen.x + Math.cos((i / 24) * Math.PI * 2) * (r + 0.15) * k, this.pen.z + Math.sin((i / 24) * Math.PI * 2) * (r + 0.15) * k));
    this.ring.position.set(this.pen.x, top + 0.06, this.pen.z);
    this.ring.renderOrder = 6;
    this.group.add(this.ring);
    this.glow = new GlowLayer(ctx.low ? 8 : 16);
    this.group.add(this.glow.mesh);
  }

  /** Every fresh sim tick: where each boar was and is, and dust behind the ones running hard. */
  tick(s: BattleState): void {
    const seen = new Set<number>();
    for (const b of s.herd?.boars ?? []) {
      seen.add(b.id);
      const hard = b.rush > 0 || b.panic > 0;
      const t = this.track.get(b.id);
      if (!t) this.track.set(b.id, { px: b.x, py: b.y, cx: b.x, cy: b.y, dist: 0, hard });
      else {
        t.dist += Math.hypot(b.x - t.cx, b.y - t.cy) * K;
        t.px = t.cx;
        t.py = t.cy;
        t.cx = b.x;
        t.cy = b.y;
        t.hard = hard;
      }
      // A stampede raises dust: a little every other tick behind each boar in it.
      if (b.rush > 0 && (s.tick + b.id) % 2 === 0) this.ctx.vfx.burst(this.ctx.at(b.x, b.y, 0.15), { n: this.ctx.low ? 2 : 4, color: DUST, speed: 0.012, up: 0.02, size: 0.32, life: 30, smoke: true, spread: 0.3 });
    }
    for (const id of [...this.track.keys()]) if (!seen.has(id)) this.track.delete(id);
    this.out = (s.herd?.boars ?? []).some((b) => !b.penned);
  }

  /** The drive's moments: one into the pen, one fallen, the herd bolting, the stampede, wolves come for a straggler. */
  event(kind: 'pen' | 'lost' | 'panic' | 'stampede' | 'wolves', x: number, y: number): void {
    const low = this.ctx.low;
    if (kind === 'pen') {
      const p = this.ctx.at(x, y, 0.4);
      this.ctx.flare(p, '#e8f0ff', 2.6, 14);
      this.ctx.vfx.burst(p, { n: low ? 6 : 12, color: '#a6aa78', speed: 0.025, up: 0.05, size: 0.07, life: 22, gravity: 0.004, spread: 0.4 });
      this.ctx.vfx.ring(this.ctx.at(x, y, 0.05), 0.2, 1.1, '#e4ecf4', 12, 0.6);
    } else if (kind === 'lost') {
      // It falls apart: chunks of grey stone and a puff of dust.
      const p = this.ctx.at(x, y, 0.3);
      this.ctx.vfx.burst(p, { n: low ? 8 : 16, color: '#727d8a', speed: 0.045, up: 0.06, size: 0.12, life: 26, gravity: 0.008, spread: 0.35 });
      this.ctx.vfx.burst(p, { n: low ? 4 : 8, color: '#4f6a3c', speed: 0.03, up: 0.04, size: 0.07, life: 22, gravity: 0.006, spread: 0.3 });
      this.ctx.vfx.burst(p, { n: low ? 3 : 6, color: DUST, speed: 0.012, up: 0.02, size: 0.45, life: 40, smoke: true, spread: 0.2 });
      this.ctx.shake(0.06);
    } else if (kind === 'panic') {
      this.ctx.vfx.burst(this.ctx.at(x, y, 0.1), { n: low ? 2 : 4, color: DUST, speed: 0.015, up: 0.015, size: 0.25, life: 22, smoke: true, spread: 0.3 });
    } else if (kind === 'stampede') {
      // The ground shakes as the herd breaks into a run.
      this.ctx.shake(0.18);
      this.ctx.vfx.ring(this.ctx.at(x, y, 0.05), 0.4, 3.2, '#c8ccd4', 20, 0.55);
    } else {
      this.ctx.vfx.ring(this.ctx.at(x, y, 0.05), 0.3, 1.6, '#ff4f3a', 18, 0.5);
    }
  }

  /** Every redraw: the boars where they are between ticks (trot, gallop or standing in the pen), their bars, the corral's ring. */
  draw(s: BattleState, alpha: number, contact: (x: number, y: number, z: number, size: number) => void, bar: (p: THREE.Vector3, frac: number) => void): void {
    const boars = s.herd?.boars ?? [];
    let n = 0;
    let legs = 0;
    const sc = SCALE.hero * BIG;
    const g = new THREE.Vector3();
    for (const b of boars) {
      if (n >= MAX) break;
      const t = this.track.get(b.id);
      const sx = t ? t.px + (t.cx - t.px) * alpha : b.x;
      const sy = t ? t.py + (t.cy - t.py) * alpha : b.y;
      g.copy(this.ctx.at(sx, sy, 0));
      const hard = !!t?.hard;
      const moving = b.moving || hard;
      // Stride from distance walked; a gallop throws the legs wider and bobs harder.
      const stride = moving ? (t?.dist ?? 0) * (hard ? 7.5 : 9.5) : 0;
      const bob = moving ? Math.abs(Math.sin(stride)) * (hard ? 0.05 : 0.025) : b.penned ? Math.max(0, Math.sin(this.now * 0.9 + b.id * 1.7)) * 0.01 : 0;
      // Head down at a gallop; in the pen, now and then one lowers its snout to root.
      const pitch = hard ? 0.12 : b.penned ? Math.max(0, Math.sin(this.now * 0.5 + b.id * 2.3)) * 0.18 : 0;
      const yaw = facingToRotY(b.facing);
      this.e.set(pitch, yaw, Math.sin(stride) * (hard ? 0.05 : 0.03), 'YXZ');
      this.q.setFromEuler(this.e);
      this.m.compose(this.v.set(g.x, g.y + bob, g.z), this.q, this.s3.set(sc, sc, sc));
      this.bodies.setMatrixAt(n, this.m);
      // Diagonal pairs swing together (a trot): front-left with back-right.
      for (let i = 0; i < 4; i++) {
        const [lx, lz] = BOAR_LEGS[i] as [number, number];
        const ph = i === 0 || i === 3 ? 1 : -1;
        this.e.set(Math.sin(stride) * (hard ? 0.75 : 0.5) * ph, yaw, 0, 'YXZ');
        this.q.setFromEuler(this.e);
        const cy = Math.cos(yaw);
        const sy2 = Math.sin(yaw);
        const ox = (lx * cy + lz * sy2) * sc;
        const oz = (-lx * sy2 + lz * cy) * sc;
        this.m.compose(this.v.set(g.x + ox, g.y + BOAR_HIP_Y * sc + bob, g.z + oz), this.q, this.s3.set(sc, sc, sc));
        this.legs.setMatrixAt(legs++, this.m);
      }
      contact(g.x, g.y, g.z, 0.62);
      if (!b.penned && b.hp < b.maxHp) bar(this.v.set(g.x, g.y + 0.72 * sc + 0.12, g.z).clone(), Math.max(0, b.hp / b.maxHp));
      n++;
    }
    this.bodies.count = n;
    this.legs.count = legs;
    this.bodies.instanceMatrix.needsUpdate = true;
    this.legs.instanceMatrix.needsUpdate = true;
    this.ring.visible = this.out || boars.length === 0;
    this.glow.begin();
    if (this.ring.visible) this.glow.add(this.pen.x, this.pen.y + 0.25, this.pen.z, PEN, 3.2, 0.06 + 0.03 * Math.sin(this.now * 2.0));
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
    this.ringMat.opacity = 0.4 + 0.15 * Math.sin(now * 2.0);
  }

  dispose(): void {
    this.bodies.geometry.dispose();
    this.bodies.dispose();
    this.legs.geometry.dispose();
    this.legs.dispose();
    this.mat.dispose();
    this.ring.geometry.dispose();
    this.ringMat.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
